import { Autocomplete, Box, Divider, Drawer, Paper, TextField } from "@mui/material"
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";
import { EquipamentosDropDown } from "../domain/models/equipamentos_dropdown";
import DynamicIcon from "./dynamic_icon";
import { DefaultFormRadioGroupInput } from "./default_form_radio_group_input";
import { DefaultFormDatePickerInput } from "./default_form_date_picker_input";
import DefaultFormInput from "./default_form_input";
import PrimaryButton from "./primary_button";
import { initialMaquinasEquipForm, MaquinasequipForm, MaquinasequipSchema } from "../../features/controledePatrimonio/presentation/schemas/Maquinasequip_cadastro/Maquinasequip_cadastro";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePageLoader } from "../contexts/page_content_context";

type Props<F extends {}> = {
  name: keyof F,
  label?: string,
  newButtonLabel?: string,
  required?: boolean,
  readOnly?: boolean,
  placeholder?: string,
  reactHookForm: UseFormReturn<F>,

};

export const DropDownEquipamentos = <F extends {},>({
  name,
  newButtonLabel,
  required,
  readOnly,
  placeholder,
  reactHookForm,

}: Props<F>) => {
  const hookForm = useForm<MaquinasequipForm>({
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
    defaultValues: initialMaquinasEquipForm,
    resolver: zodResolver(MaquinasequipSchema),
  });

  const {
    commons: { geEquipamentosDropDownUseCase },
    controlePatrimonio: { createControlePatrimonioUseCase },
  } = useDependencyInjection();

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();
  const [equipamentosDropDown, setEquipamentosDropDown] = useState<EquipamentosDropDown[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const loadEquipamentoDropDown = useCallback(async () => {
    try {
      const equimentos = await geEquipamentosDropDownUseCase.getEquipamentosDropDown();
      setEquipamentosDropDown(equimentos);
    } catch (e: unknown) {
      console.error("Erro ao carregar equipamentos:", e);
    }
  }, [geEquipamentosDropDownUseCase]);

  useEffect(() => {
    loadEquipamentoDropDown();
  }, [loadEquipamentoDropDown]);

  const CustomPaper = useMemo(
    () =>
      ({ children, ...props }: any) => (
        <Paper {...props}>
          <div>
            {children}
            <Divider />
            <div>
              <div
                className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold flex items-center gap-2 text-sm text-primary-green hover:bg-hover-light-gray"
                onMouseDown={() => setOpenModal(true)}
                role="button"
                tabIndex={0}>
                <DynamicIcon
                  nameIcon="Add"
                  size={20}
                  className="text-primary-green"
                />
                <span>{newButtonLabel ?? "Novo"}</span>
              </div>
            </div>
          </div>
        </Paper>
      ),
    [newButtonLabel]
  );
  const hanldleSalvarEquipamento = useCallback(async (form: MaquinasequipForm) => {
    try {
      setSaving(true);
      const idCargo = await createControlePatrimonioUseCase
        .createControlePatrimonio(
          form.ativo,
          form.dataCadastro,
          form.codigo,
          form.idCentroCusto,
          form.descricao,
          form.criterioAceitacao,
          form.referenciaAceitacao,
          form.quantidade,
          form.dataAquisicao,
          form.dataGarantia,
          form.descResponsavelMonit,
          form.idFuncionarioMonit,
          form.idFornecedorMonit,
          form.descResponsavelResp,
          form.idFuncionarioResp,
          form.tipoMonitoramento,
          form.frequenciaVerificacao,
          form.dataProxVerificacao,
          form.dataVerificacao,
          form.marca,
          form.modelo,
          form.valor,
          form.tipo,
          form.idFornecedor,
          form.status,
          form.fatorGerador,
          form.faixaDeUso,
          form.localizacao,
          form.observacao,
          form.tipoCalibradoMonit
        );

      await loadEquipamentoDropDown();

      reactHookForm.setValue(name as any, idCargo as any);
      pushInfoNotification("SUCESSO", "Equipamento criado com sucesso!!", { isAutoFinish: true })

    } catch (e: unknown) {
      pushErrorNotification("Erro", ["Falha ao salvar equipamento"], { isAutoFinish: true })
    } finally {
      setSaving(false);
      setOpenModal(false);
    }
  }, [createControlePatrimonioUseCase, loadEquipamentoDropDown, reactHookForm, name, pushInfoNotification, pushErrorNotification]);

  const ativoOptions = useMemo(() => {
    return [
      { id: 1, label: "Sim" },
      { id: 0, label: "Não" },
    ];
  }, []);
  // const tipoOptions = useMemo(() => {
  //   return [
  //     { id: 0, label: "Funcionário" },
  //     { id: 1, label: "Fornecedor" },
  //   ];
  // }, []);
  // const monitoramentoOptions = useMemo(() => {
  //   return [
  //     {
  //       id: 1,
  //       label: "Calibrado",
  //       style: {
  //         backgroundColor: "#FFD700",
  //         padding: "5px",
  //         borderRadius: "4px",
  //         color: 'black',
  //         fontWeight: 'bold',
  //       }
  //     },
  //     {
  //       id: 0,
  //       label: "Verificação",
  //       style: {
  //         backgroundColor: "#00A000",
  //         padding: "5px",
  //         borderRadius: "4px",
  //         color: 'black',
  //         fontWeight: 'bold',
  //       }
  //     },
  //   ];
  // }, [ ]);



  return (
    <>
      <Controller
        name={name as any}
        control={reactHookForm.control}
        render={({ field, formState }) => {
          let helperText: string | null = null;

          if (formState.errors[name as keyof typeof formState.errors]) {
            const fieldError = formState.errors[name] as any;
            if (fieldError?.message && typeof fieldError.message === 'string') {
              helperText = fieldError.message;
            }
          }


          return (
            <Autocomplete
              fullWidth={true}
              size="small"
              disablePortal
              options={equipamentosDropDown}
              getOptionKey={opt => opt.id}
              getOptionLabel={(opt) => opt.title}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              sx={{ minHeight: 45 }}
              readOnly={readOnly}
              disabled={formState.disabled}
              value={equipamentosDropDown.find(x => x.id === field.value) ?? null}
              slots={{
                paper: CustomPaper,
              }}
              ListboxProps={{
                sx: { maxHeight: 200 }
              }}
              onChange={(_, newValue) => { field.onChange(newValue ? newValue.id : undefined); }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Maquina equipamentos"
                  error={helperText !== null}
                  helperText={helperText}
                  placeholder={placeholder}
                  required={required}
                />
              )}
            />
          );
        }}
      />

      <Drawer
        anchor="right"
        open={openModal}
        onClose={() => setOpenModal(false)}
        style={{ zIndex: 1301 }}
        PaperProps={{ style: { width: 500, padding: 20, display: "flex", flexDirection: "column", height: "100%" } }}
      >
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <h2 className="text-lg font-semibold mb-4">Adicionar Equipamento</h2>

          <div className="flex flex-col w-full gap-4">

            <DefaultFormRadioGroupInput
              name='ativo'
              label='Ativo'
              options={ativoOptions}
              reactHookForm={hookForm}
            />

            <DefaultFormDatePickerInput
              name="dataCadastro"
              label="Data de Cadastro"
              reactHookForm={hookForm}
            />

            <DefaultFormInput
              name="codigo"
              label="Código"
              required
              reactHookForm={hookForm}
            />

            <DefaultFormInput
              name="descricao"
              label="Nome do Patrimônio"
              required={true}
              multiline={true}
              rows={5}
              reactHookForm={hookForm}
            />

            {/* <DefaultFormRadioGroupInput
              name="tipoCalibradoMonit"
              label="Calibrado / Verificado Por"
              options={tipoOptions}
              reactHookForm={hookForm}
              onChangeValue={value => {
                hookForm.setValue('idFornecedorMonit', null);
                hookForm.setValue('idFuncionarioMonit', null);
                hookForm.setValue('descResponsavelMonit', null);
              }}
            />

            <DefaultFormRadioGroupInput
              name="tipoMonitoramento"
              label="Tipo Monitoramento"
              options={monitoramentoOptions}
              reactHookForm={hookForm}
            /> */}

          </div>
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "white",
            padding: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "16px"
          }}
        >
          <Box
            className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
            onClick={() => setOpenModal(false)}
          >
            <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
          </Box>

          <Box width="120px">
            {saving ? (
              <PrimaryButton buttonColor="primary-green">
                Salvando...
              </PrimaryButton>
            ) : (
              <PrimaryButton
                buttonColor="primary-green"
                onClick={hookForm.handleSubmit(hanldleSalvarEquipamento)}
              >
                Salvar
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
